import isNil from 'lodash/isNil';

import getCexTickerFallback from '@/entities/cex-ticker/api/cex-ticker-fallback';

import { IAccountBalances } from 'types/accounts';
import { IPnl } from 'types/pnl';
import { TradingPositionPoint } from 'types/tradingPosition';
import { ECexName, IRange } from 'web3';

import { IStatisticToken, IStatisticTransaction } from './models';

import { AApiBase } from '../apiBase/AApiBase';
import { metricsAxios } from '../apiBase/axios';
import type { IBaseResponse } from '../apiBase/IBaseResponse';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';

interface IStatisticResponse {
  first_trade_time: string;
  transactions: IStatisticTransaction[];
  tokens: IStatisticToken[];
}

class ApiStatistic extends AApiBase {
  protected baseUrl = '/api';
  protected withHeader = true;

  private static __instance: ApiStatistic | undefined;
  private static get instance(): ApiStatistic {
    if (ApiStatistic.__instance === undefined) {
      ApiStatistic.__instance = new ApiStatistic();
    }
    return ApiStatistic.__instance;
  }

  public static getStatistic = async ({
    projects_ids,
    pair_ids,
    token_ids,
    ranges,
  }: {
    projects_ids?: number[];
    pair_ids?: number[];
    token_ids?: number[];
    ranges: (IRange | string)[];
  }) => {
    try {
      const urlParams = new URLSearchParams();
      if (projects_ids && projects_ids.length !== 0) {
        for (const projectId of projects_ids) {
          urlParams.append('project_ids', projectId.toString());
        }
      }

      if (pair_ids && pair_ids.length !== 0) {
        for (const pairId of pair_ids) {
          urlParams.append('pair_ids', pairId.toString());
        }
      }
      if (token_ids && token_ids.length !== 0) {
        for (const tokenId of token_ids) {
          urlParams.append('token_ids', tokenId.toString());
        }
      }

      for (const range of ranges) {
        urlParams.append('range', range.toString());
      }

      const response = await ApiStatistic.instance.get<IBaseResponse<IStatisticResponse>>(
        '/statistics' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<IStatisticResponse>({ error });
    }
  };

  /**
   * @deprecated Use entities/cex-statistic/api instead
   */
  public static getCexAvgOrderSize = async ({
    pairId,
    period,
    startDate,
    endDate,
    account_id,
  }: {
    pairId: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
    account_id?: number[];
  }) => {
    type Return = {
      base: string;
      quote: string;
    };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/avg/size' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated Use entities/cex-statistic/api instead
   */
  public static getCexOurTradesCount = async ({
    pairId,
    period,
    startDate,
    endDate,
    account_id,
  }: {
    pairId: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
    account_id?: number[];
  }) => {
    type Return = {
      count: string;
    };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/count/our/trade' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated Use entities/project/hooks/useTotalVolumeOurTrades.ts instead
   */
  public static getCexOurTradesTotalVolume = async ({
    pairId,
    period,
    startDate,
    endDate,
    account_id,
  }: {
    pairId: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
    account_id?: number[];
  }) => {
    type Return = {
      our_base: string;
      our_quote: string;
      our_base_per_quote: string;
    };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v2/total/our/trade' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated Use entities/cex-statistic/api instead
   */
  public static getCexTotalFees = async ({
    pairId,
    period,
    startDate,
    endDate,
    account_id,
  }: {
    pairId: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
    account_id?: number[];
  }) => {
    type Return = { fee: string };

    try {
      const urlParams = new URLSearchParams();

      if (pairId) {
        for (const id of pairId) {
          urlParams.append('pair_id', id.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/total/our/fee' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexOurIncome = async ({
    pairId,
    period,
    startDate,
    endDate,
    account_id,
  }: {
    pairId: number;
    period?: string;
    startDate?: number;
    endDate?: number;
    account_id?: number[];
  }) => {
    type Return = { income: { base: string; quote: string } };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('pair_id', pairId.toString());
      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }
      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/total/our/income' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated use src/entities/cex-ticker/api/queries/useGetCexTicker.ts instead
   */
  public static getCexTickerInfo = async ({ symbol, cex }: { symbol: string; cex: ECexName }) => {
    type Return = {
      base_volume: string;
      change24h: string;
      high24h: string;
      last_price: string;
      low24h: string;
      quote_volume: string;
      spread_percentage: string;
    };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);
      urlParams.append('cex', cex);

      const response = await metricsAxios.get<Return>(
        '/api/v1/market/ticker' + '?' + urlParams.toString(),
      );

      return mapResponse({ data: response.data });
    } catch (error: unknown) {
      return getCexTickerFallback({ symbol, cex });
    }
  };

  /**
   * @deprecated Use entities/project/hooks/useTotalVolume.ts instead
   */
  public static geCexMarketTotalVolume = async ({
    symbol,
    cex,
    period,
    startDate,
    endDate,
  }: {
    symbol: string;
    cex: ECexName[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = {
      total_volume_base: string;
      total_volume_quote: string;
    };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/market/total-volume' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  /**
   * @deprecated Use entities/cex-statistic/api instead
   */
  public static geCexActualBalance = async ({
    symbol,
    cex,
    account_id,
  }: {
    symbol: string;
    cex: ECexName;
    account_id?: number[];
  }) => {
    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);
      urlParams.append('cex', cex);

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<IAccountBalances>(
        `/api/v1/balance/actual?${urlParams.toString()}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<IAccountBalances>({ error });
    }
  };

  public static geCexActualProjectBalance = async ({
    symbol,
    cex,
    account_id,
  }: {
    symbol: string;
    cex?: ECexName;
    account_id?: number[];
  }) => {
    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);

      if (cex) {
        urlParams.append('cex', cex);
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<IAccountBalances>(
        `/api/v1/balance/actual/project?${urlParams.toString()}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<IAccountBalances>({ error });
    }
  };

  /**
   * @deprecated Use entities/cex-statistic/api instead
   */
  public static geCexBalancesDelta = async ({
    symbol,
    cex,
    account_id,
    period,
    startDate,
    endDate,
  }: {
    symbol: string;
    cex: ECexName[];
    account_id?: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    type Return = {
      ending_balance: {
        base: {
          amount: string;
          amount_usd: string;
          coin: string;
        };
        quote: {
          amount: string;
          amount_usd: string;
          coin: string;
        };
      };
      starting_balance: {
        base: {
          amount: string;
          amount_usd: string;
          coin: string;
        };
        quote: {
          amount: string;
          amount_usd: string;
          coin: string;
        };
      };
    };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('symbol', symbol);

      if (cex) {
        for (const cexName of cex) {
          urlParams.append('cex', cexName.toString());
        }
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      const response = await metricsAxios.get<Return>(
        '/api/v1/balance/delta' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getCexUptime = async ({ pairId }: { pairId: number }) => {
    type Return = {
      uptime: string;
    };

    try {
      const urlParams = new URLSearchParams();

      urlParams.append('pair_id', pairId.toString());

      const response = await metricsAxios.get<Return>(
        '/api/v1/uptime/actual' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getBalancePnl = async ({
    pairId,
    symbol,
    account_id,
    cex,
    period,
    startDate,
    endDate,
  }: {
    pairId: number;
    symbol: string;
    account_id: number[];
    cex: string;
    period?: string;
    startDate?: number;
    endDate?: number;
  }) => {
    try {
      const urlParams = new URLSearchParams();

      urlParams.append('cex', cex);
      urlParams.append('symbol', symbol);
      urlParams.append('pair_id', pairId.toString());

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      if (period) {
        urlParams.append('period', period);
      }
      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      const response = await metricsAxios.get<IPnl>(
        '/api/v1/balance/pnl' + '?' + urlParams.toString(),
      );
      return mapResponse({ data: response.data });
    } catch (error: unknown) {
      return mapError<IPnl>({ error });
    }
  };

  public static geBalanceTradingPosition = async ({
    pairId,
    account_id,
    period,
    startDate,
    endDate,
    bots,
    rqe,
  }: {
    pairId: number;
    account_id: number[];
    period?: string;
    startDate?: number;
    endDate?: number;
    bots: string[];
    rqe: boolean;
  }) => {
    try {
      const urlParams = new URLSearchParams();

      urlParams.append('pair_id', pairId.toString());
      urlParams.append('rqe', rqe.toString());

      if (bots) {
        for (const botId of bots) {
          urlParams.append('bots', botId.toString());
        }
      } else {
        urlParams.append('bots', ['all_bots'].toString());
      }

      if (period) {
        urlParams.append('period', period === '24h' ? '1d' : period === '168h' ? '1w' : period);
      }

      if (!isNil(startDate) && !isNil(endDate)) {
        urlParams.append('from', startDate.toString());
        urlParams.append('to', endDate.toString());
      }

      if (account_id) {
        for (const accountId of account_id) {
          urlParams.append('account_id', accountId.toString());
        }
      }

      const response = await metricsAxios.get<{
        points: TradingPositionPoint[];
      }>(`/api/v1/balance/trading-position?${urlParams.toString()}`);

      return mapResponse({ data: response.data });
    } catch (error: unknown) {
      return mapError<{
        points: TradingPositionPoint[];
      }>({ error });
    }
  };
}

export { ApiStatistic };
